// ============================================================================
// KLINISOL CRO - Home Page 
// COLOR SCHEME: Slate Grey (#475569) & Magenta (#E91E63)
// BRAND FEEL: Sophisticated, Modern Medical, Distinctive & Professional
// NOTE: This breaks away from typical healthcare blues - very memorable!
// ============================================================================

import React from 'react';
import { Link } from 'react-router-dom';
import { 
    FileText, Hospital, BarChart3, CheckCircle, Shield, AlertTriangle,
    Heart, Stethoscope, Activity, Brain, Mic, TrendingUp, Lock, Target,
    Sparkles, MessageCircle, Award, Zap
} from 'lucide-react';

const Home = () => {
    return (
        <div className="min-h-screen bg-[#f8fafd]">
            {/* Hero Section */}
            <section className="bg-gradient-to-r from-[#475569] via-[#E91E63] to-[#475569] text-white py-20 px-6">
                <div className="max-w-7xl mx-auto text-center">
                    <h1 className="text-5xl md:text-6xl font-bold mb-4">KLINiSOL</h1>
                    <p className="text-xl mb-8 max-w-3xl mx-auto">
                        Leading the way in clinical research with comprehensive CRO services for investigator-initiated trials across therapeutic areas.
                    </p>
                    <div className="flex flex-wrap justify-center gap-4">
                        <Link to="/services" className="inline-flex items-center gap-3 px-6 py-3 bg-white/10 backdrop-blur-sm rounded-lg shadow-lg hover:shadow-xl border-2 border-white hover:bg-white/20 transition-all group">
                            <Award className="w-6 h-6 text-white group-hover:scale-110 transition-transform" />
                            <span className="font-bold text-white">Our Services</span>
                        </Link>
                        <Link to="/contact" className="inline-flex items-center gap-3 px-6 py-3 bg-white/10 backdrop-blur-sm rounded-lg shadow-lg hover:shadow-xl border-2 border-white hover:bg-white/20 transition-all group">
                            <MessageCircle className="w-6 h-6 text-white group-hover:scale-110 transition-transform" />
                            <span className="font-bold text-white">Let's Connect</span>
                        </Link>
                    </div>
                </div>
            </section>

            {/* Services Overview */}
            <section className="py-16 px-6">
                <div className="max-w-7xl mx-auto">
                    <div className="text-center mb-12">
                        <h2 className="text-3xl font-bold text-[#475569] mb-3">Comprehensive CRO Services</h2>
                        <p className="text-lg text-gray-600">Full-service support for investigator-initiated clinical trials</p>
                    </div>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
                        {[
                            { icon: FileText, title: "Protocol Development", desc: "Expert protocol design and regulatory strategy for IIT success" },
                            { icon: Hospital, title: "Site Management", desc: "Complete site selection, initiation, and monitoring services" },
                            { icon: BarChart3, title: "Data Management", desc: "FDA-ready data capture, cleaning, and analysis" },
                            { icon: CheckCircle, title: "Regulatory Affairs", desc: "IND/IDE submissions, IRB management, and FDA compliance" },
                            { icon: Shield, title: "Quality Assurance", desc: "ICH-GCP aligned QA oversight and audit readiness" },
                            { icon: AlertTriangle, title: "Safety Monitoring", desc: "Comprehensive pharmacovigilance and AE/SAE management" }
                        ].map((service, index) => (
                            <div key={index} className="bg-white rounded-xl p-6 shadow-sm hover:shadow-md border border-gray-100 transition-all hover:border-[#E91E63]/50">
                                <service.icon className="w-12 h-12 text-[#E91E63] mb-4" />
                                <h3 className="text-xl font-semibold text-gray-900 mb-2">{service.title}</h3>
                                <p className="text-gray-600">{service.desc}</p>
                            </div>
                        ))}
                    </div>
                    
                    <div className="text-center">
                        <Link to="/services" className="inline-flex items-center gap-3 px-6 py-3 bg-white rounded-lg shadow-md hover:shadow-xl border-2 border-[#475569] hover:border-[#E91E63] transition-all group">
                            <Award className="w-6 h-6 text-[#475569] group-hover:text-[#E91E63] transition-colors" />
                            <span className="font-bold text-gray-900">View All Services</span>
                        </Link>
                    </div>
                </div>
            </section>

            {/* Therapeutic Areas */}
            <section className="py-16 px-6 bg-white">
                <div className="max-w-7xl mx-auto">
                    <div className="text-center mb-12">
                        <h2 className="text-3xl font-bold text-[#475569] mb-3">Therapeutic Areas of Expertise</h2>
                        <p className="text-lg text-gray-600">Specialized experience in three key therapeutic areas</p>
                    </div>
                    
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
                        {[
                            { icon: Activity, title: "Dermatology", desc: "Psoriasis, eczema, acne, skin cancer, wound healing, and rare skin diseases" },
                            { icon: Heart, title: "Cardiovascular", desc: "Heart failure (HFrEF/HFpEF), arrhythmias, hypertension, CAD, dyslipidemia" },
                            { icon: Stethoscope, title: "Gastroenterology", desc: "IBD (Crohn's/UC), IBS, GERD, hepatology (NASH, cirrhosis), celiac disease" }
                        ].map((area, index) => (
                            <div key={index} className="bg-gradient-to-br from-[#475569]/5 to-[#E91E63]/5 rounded-xl p-8 border border-gray-200 hover:border-[#E91E63] hover:shadow-lg transition-all">
                                <area.icon className="w-14 h-14 text-[#E91E63] mb-4" />
                                <h3 className="text-2xl font-bold text-gray-900 mb-3">{area.title}</h3>
                                <p className="text-gray-700">{area.desc}</p>
                            </div>
                        ))}
                    </div>
                    
                    <div className="text-center">
                        <Link to="/therapeutic-areas" className="inline-flex items-center gap-3 px-6 py-3 bg-white rounded-lg shadow-md hover:shadow-xl border-2 border-[#475569] hover:border-[#E91E63] transition-all group">
                            <Award className="w-6 h-6 text-[#475569] group-hover:text-[#E91E63] transition-colors" />
                            <span className="font-bold text-gray-900">Learn More</span>
                        </Link>
                    </div>
                </div>
            </section>

            {/* Why Choose KLINISOL */}
            <section className="py-16 px-6">
                <div className="max-w-7xl mx-auto">
                    <h2 className="text-3xl font-bold text-[#475569] text-center mb-12">
                        Why Choose KLIN<span className="text-[#E91E63]">i</span>SOL?
                    </h2>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                        {[
                            { number: "01", title: "IIT-Only", desc: "100% focused on investigator-initiated trials" },
                            { number: "02", title: "Always Audit Ready", desc: "FDA 21 CFR Part 11 compliant by design" },
                            { number: "03", title: "Derm-Cardio-GI", desc: "Three therapeutic areas, deep expertise" },
                            { number: "04", title: "Blockchain-Secured", desc: "Tamper-proof certificates, instant QR verification" }
                        ].map((item, index) => (
                            <div key={index} className="bg-white rounded-xl p-6 shadow-sm border border-gray-100 hover:shadow-lg hover:border-[#E91E63] transition-all">
                                <div className="text-5xl font-bold text-[#E91E63] mb-4 opacity-20">{item.number}</div>
                                <h3 className="text-xl font-bold text-gray-900 mb-3">{item.title}</h3>
                                <p className="text-gray-600">{item.desc}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* ILO Platform Section */}
            <section className="py-16 px-6 bg-gradient-to-r from-[#475569] to-[#E91E63] text-white">
                <div className="max-w-5xl mx-auto">
                    <div className="text-center mb-8">
                        <Sparkles className="w-16 h-16 mx-auto mb-4" />
                        <h2 className="text-4xl font-bold mb-4">
                            Introducing the KLIN<span className="text-[#E91E63]">i</span>SOL ILO Platform
                        </h2>
                        <p className="text-lg opacity-90 mb-8 max-w-3xl mx-auto">
                            AI-driven clinical research ecosystem uniting EDC, eTMF, IRT, CTMS, and AI Safety Monitoring designed for 21 CFR Part 11 compliance. Experience the future of unified, intelligent trial management.
                        </p>
                        
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
                            {[
                                { icon: Brain, text: "AI-Powered Analytics" },
                                { icon: Mic, text: "Voice-Enabled Data Capture" },
                                { icon: TrendingUp, text: "Real-Time Dashboards" },
                                { icon: Lock, text: "FDA-Ready" }
                            ].map((feature, index) => (
                                <div key={index} className="bg-white/10 backdrop-blur-sm rounded-lg p-4 border border-white/20 hover:border-[#E91E63] hover:bg-white/20 transition-all">
                                    <feature.icon className="w-8 h-8 mx-auto mb-2" />
                                    <p className="text-sm font-medium">{feature.text}</p>
                                </div>
                            ))}
                        </div>

                        <div className="flex flex-col items-center gap-6 mt-8">
                            <div className="inline-flex items-center gap-2 bg-white/20 backdrop-blur-sm px-4 py-2 rounded-full border border-white/30">
                                <Target className="w-5 h-5" />
                                <span className="font-semibold">Pre-Launch • Pilot Deployment 2026</span>
                            </div>
                            
                            <Link to="/ilo-platform" className="inline-flex items-center gap-3 px-8 py-4 bg-white rounded-lg shadow-xl hover:shadow-2xl border-2 border-[#475569] hover:border-[#E91E63] transition-all hover:scale-105 group">
                                <Zap className="w-8 h-8 text-[#475569] group-hover:text-[#E91E63] transition-colors" />
                                <span className="text-lg font-bold text-[#475569] group-hover:text-[#E91E63] transition-colors">Explore ILO Platform</span>
                            </Link>
                        </div>
                    </div>
                </div>
            </section>

            {/* Final CTA Section */}
            <section className="py-16 px-6 bg-gradient-to-br from-[#475569]/10 to-[#E91E63]/10">
                <div className="max-w-4xl mx-auto text-center">
                    <h2 className="text-4xl font-bold text-[#475569] mb-4">Ready to Advance Your Research?</h2>
                    <p className="text-lg text-gray-700 mb-8">
                        Let's explore how KLIN<span className="text-[#E91E63]">i</span>SOL's therapeutic expertise and operational capabilities can accelerate your investigator-initiated or sponsor-supported study
                    </p>
                    <Link to="/contact" className="inline-flex items-center gap-3 px-8 py-4 bg-white rounded-lg shadow-xl hover:shadow-2xl border-2 border-[#475569] hover:border-[#E91E63] transition-all hover:scale-105 group">
                        <MessageCircle className="w-8 h-8 text-[#475569] group-hover:text-[#E91E63] transition-colors" />
                        <span className="text-xl font-bold text-[#475569]">Let's Connect</span>
                    </Link>
                </div>
            </section>

            {/* Footer */}
            <footer className="bg-gradient-to-r from-[#475569] via-[#E91E63] to-[#475569] text-white py-12 px-6">
                <div className="max-w-7xl mx-auto">
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-8 mb-8">
                        <div>
                            <h3 className="text-2xl font-bold mb-2">
                                KLIN<span className="text-[#E91E63]">i</span>SOL
                            </h3>
                            <p className="text-sm opacity-90">Advancing Investigator-Initiated Research</p>
                        </div>
                        
                        <div>
                            <h4 className="font-semibold mb-3">Quick Links</h4>
                            <ul className="space-y-2 text-sm">
                                <li><Link to="/services" className="hover:text-[#E91E63] transition-colors">Services</Link></li>
                                <li><Link to="/ilo-platform" className="hover:text-[#E91E63] transition-colors">ILO Platform</Link></li>
                                <li><Link to="/therapeutic-areas" className="hover:text-[#E91E63] transition-colors">Therapeutic Areas</Link></li>
                                <li><Link to="/about" className="hover:text-[#E91E63] transition-colors">About Us</Link></li>
                                <li><Link to="/contact" className="hover:text-[#E91E63] transition-colors">Contact</Link></li>
                            </ul>
                        </div>
                        
                        <div>
                            <h4 className="font-semibold mb-3">Resources</h4>
                            <ul className="space-y-2 text-sm">
                                <li><Link to="/resources" className="hover:text-[#E91E63] transition-colors">Research Resources</Link></li>
                                <li><Link to="/for-investigators" className="hover:text-[#E91E63] transition-colors">For Investigators</Link></li>
                                <li><Link to="/news-kiosk" className="hover:text-[#E91E63] transition-colors">News Kiosk</Link></li>
                                <li><Link to="/terms-and-conditions" className="hover:text-[#E91E63] transition-colors">Terms & Conditions</Link></li>
                            </ul>
                        </div>
                        
                        <div>
                            <h4 className="font-semibold mb-3">For Team Members</h4>
                            <ul className="space-y-2 text-sm">
                                <li><Link to="/dashboard" className="hover:text-[#E91E63] transition-colors">Training Portal</Link></li>
                                <li><Link to="/dashboard" className="hover:text-[#E91E63] transition-colors">SOP Library</Link></li>
                                <li><Link to="/login" className="hover:text-[#E91E63] transition-colors">Login</Link></li>
                            </ul>
                        </div>
                    </div>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-8">
                        <div>
                            <h4 className="font-semibold mb-3">Contact</h4>
                            <p className="text-sm opacity-90 mb-1">600 Broadway Ste 200 #2189</p>
                            <p className="text-sm opacity-90 mb-2">Albany, NY 12207</p>
                            <p className="text-sm opacity-90 mb-1">Email: info@klinisol.com</p>
                            <p className="text-sm opacity-90">Website: klinisol.com</p>
                        </div>
                    </div>
                    
                    <div className="border-t border-white/20 pt-6 text-center">
                        <p className="text-sm opacity-90 mb-4">&copy; 2025 KLINISOL. All rights reserved.</p>
                        
                        {/* Social Media Links */}
                        <div className="flex items-center justify-center gap-4">
                            <a href="https://www.linkedin.com/company/klinisol/" target="_blank" rel="noopener noreferrer" className="w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center transition-all hover:scale-110" aria-label="LinkedIn">
                                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                                    <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
                                </svg>
                            </a>
                            
                            <a href="https://youtube.com/@klinisol721" target="_blank" rel="noopener noreferrer" className="w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center transition-all hover:scale-110" aria-label="YouTube">
                                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                                    <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
                                </svg>
                            </a>
                        </div>
                    </div>
                </div>
            </footer>
        </div>
    );
};

export default Home;